import { useEvent } from 'expo';
import { useVideoPlayer, VideoView } from 'expo-video';
import { View, YStack } from 'tamagui';

import PSButton from '@/components/ui/PSButton';
import PSIcon from '@/components/ui/PSIcon';
import { usePlayWhileFocused } from '@/src/hooks/usePlayWhileFocused';
import { SHOWREEL_URL, SHOWREEL_VIDEO_STYLE } from './PSUpsellBanner';
import PSText from './ui/PSText';
import AccessPassIntro from './AccessPassIntro';

// Portrait 3:4, the house ratio (EventCard / ImageCarousel / PSUpsellBanner).
const RATIO = 3 / 4;

/**
 * The Access Pass landing page's hero: the showreel, then the pitch line and one
 * CTA BELOW it — which is what makes it a different component from
 * [[PSUpsellBanner]], where both sit over the video. Same reel, same
 * autoplay-muted-loop-with-tap-to-unmute behaviour.
 *
 * Takes the finished pitch line rather than a price: only the screen knows the
 * catalog, and the line is the cheapest plan's price.
 */
export default function AccessPassShowreel({
  titleString,
  ctaTitle,
  onPressCta,
}: {
  /** The finished pitch line, price included — e.g. "starts at ₹2,450/month". */
  titleString: string;
  ctaTitle: string;
  onPressCta?: () => void;
}) {
  const player = useVideoPlayer(SHOWREEL_URL, (p) => {
    p.loop = true;
    // Muted BEFORE play(): an unmuted autoplay is refused outright on web.
    p.muted = true;
    p.play();
  });

  const { muted } = useEvent(player, 'mutedChange', { muted: player.muted });

  usePlayWhileFocused(player);

  return (
    <YStack>
      <View alignSelf="stretch" aspectRatio={RATIO} overflow="hidden">
        <VideoView
          style={SHOWREEL_VIDEO_STYLE}
          player={player}
          contentFit="cover"
          nativeControls={false}
          // Web-only: keep the reel in its box instead of the browser hijacking
          // it into a fullscreen player.
          playsInline
        />

        {/* The OVERLAY owns the mute tap, not the VideoView — whether a native
            video surface passes touches through is platform-dependent. */}
        <YStack
          flex={1}
          p="$lg"
          justifyContent="flex-end"
          items="flex-end"
          cursor="pointer"
          // `player` is a native SharedObject: assigning `muted` IS expo-video's
          // setter, which the compiler's mutability rule can't tell from
          // mutating a memoized object.
          // eslint-disable-next-line react-hooks/immutability
          onPress={() => (player.muted = !muted)}
        >
          <PSIcon
            name={muted ? 'volume-mute' : 'volume-high'}
            color="$textWhite"
          />
        </YStack>
      </View>

      {/* Re-inset from the screen's bleed: the reel runs edge to edge, the pitch
          lines up with the page gutter. */}
      <YStack px="$lg" pt="$lg" gap={'$space.lg'} items="flex-start">
        <AccessPassIntro />
        <PSButton
          title={ctaTitle}
          onPress={onPressCta}
          fullWidth
          icon={<PSIcon name="arrow-forward" color="$textWhite" size={18} />}
        />
      </YStack>
    </YStack>
  );
}
